import CurrencyView from '@app/components/price/currency-view';
import { getFragmentData } from '@app/graphql/types';
import { BranchItemFragment, MenuProductItemFragment, ProductConfigurationType } from '@app/graphql/types/graphql.ts';
import {
  MenuProductConfigurationFragment,
  MenuProductConfigurationValueFragment,
} from '@app/page/online-order/model/menu-product-fragment.ts';
import { calculatePickedProduct } from '@app/page/online-order/product-picker/logic/calculate-picked-product.ts';
import { useProductPicker } from '@app/page/online-order/product-picker/logic/use-product-picker.ts';
import CDNLink from '@app/utils/cdn-link.ts';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  Portal,
  RadioGroup,
  Text,
  useBreakpointValue,
  VStack,
} from '@chakra-ui/react';
import { XCircleIcon } from '@heroicons/react/20/solid';
import { useTranslate } from '@tolgee/react';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { CiCircleMinus, CiCirclePlus, CiShoppingCart } from 'react-icons/ci';

const ProductConfigurationSetupV2 = (props: {
  product: MenuProductItemFragment;
  children: ReactNode;
  onClick?: () => void;
  branch?: BranchItemFragment;
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { product } = props;
  const { t } = useTranslate();

  const image =
    product.images.length > 0 ? product.images[0] : props.branch?.company.logo ? props.branch.company.logo : '';
  const configurations = getFragmentData(MenuProductConfigurationFragment, product.configurations);

  const addToCart = useProductPicker((state) => state.add);

  const [configurationValues, setConfigurationValues] = useState<string[]>([]);
  const [count, setCount] = useState<number>(1);

  const getDefaltConfigutationId = useCallback(() => {
    const configurations = getFragmentData(MenuProductConfigurationFragment, product.configurations);

    const baseConfiguration = configurations.filter((config) => config.type == ProductConfigurationType.Base);
    const baseValue = getFragmentData(MenuProductConfigurationValueFragment, baseConfiguration[0].values[0]);

    const selectionConfiguration = configurations.filter((config) => config.type == ProductConfigurationType.Selection);
    if (selectionConfiguration.length > 0) {
      const selectionValue = getFragmentData(
        MenuProductConfigurationValueFragment,
        selectionConfiguration[0].values[0]
      );
      return [baseValue.id, selectionValue.id];
    }

    return [baseValue.id];
  }, [product.configurations]);

  useEffect(() => {
    const defaultConfigId = getDefaltConfigutationId();
    setConfigurationValues(defaultConfigId);
  }, [getDefaltConfigutationId, product.configurations]);

  const validate = () => {
    for (const config of product.configurations) {
      const configuration = getFragmentData(MenuProductConfigurationFragment, config);
      const values = getFragmentData(MenuProductConfigurationValueFragment, configuration.values);
      const idValues = values.map((value) => value.id);

      const requiredAtLeastOne =
        configuration.type === ProductConfigurationType.Base ||
        configuration.type === ProductConfigurationType.Selection;

      if (requiredAtLeastOne && configurationValues.filter((id) => idValues.includes(id)).length !== 1) {
        return false;
      }
    }

    return true;
  };

  const total = calculatePickedProduct(configurationValues, product, count);

  const addToCartHandler = () => {
    if (!validate()) {
      return;
    }

    addToCart({
      productID: product.id,
      quantity: count,
      configurations: configurationValues
        .map((valueID) => {
          const configuration = configurations.find((config) =>
            getFragmentData(MenuProductConfigurationValueFragment, config.values).some((value) => value.id === valueID)
          );

          if (!configuration) {
            return undefined;
          }

          const price =
            configurations
              .flatMap((config) => getFragmentData(MenuProductConfigurationValueFragment, config.values))
              .find((value) => {
                return value.id == valueID;
              })?.price ?? 0;

          return {
            configurationID: configuration.id,
            valueID: valueID,
            price,
          };
        })
        .filter((v) => v !== undefined) as { configurationID: string; valueID: string }[],
    });

    setConfigurationValues(getDefaltConfigutationId);
  };

  return (
    <Dialog.Root placement={'center'} size={isMobile ? 'full' : 'md'}>
      <Dialog.Trigger asChild>{props.children}</Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content className="product-config-chakra__modal">
            <Dialog.ActionTrigger asChild>
              <Box position={'absolute'} top={'16px'} right={'16px'}>
                <Icon>
                  <XCircleIcon
                    style={{
                      width: '30px',
                      height: '30px',
                      stroke: 'white',
                    }}
                  />
                </Icon>
              </Box>
            </Dialog.ActionTrigger>

            <Dialog.Body rounded={'lg'} border={'4px solid'} borderColor={'white'} overflow={'hidden'} w={'full'} p={0} className="product-config-chakra__content">
              <VStack align={'stretch'}>
                <VStack mt={'0'} align={'center'}>
                  {
                    <Box
                      p={0}
                      w="full"
                      maxW={{ base: '100vw', md: '550px' }}
                      h={{ base: '220px', md: '260px' }}
                      overflow="hidden"
                    >
                      <Image w={'full'} h={'full'} src={CDNLink(image)} alt="logo" objectFit="cover" />
                    </Box>
                  }
                  <VStack gap={4} mt={'4'} align={'center'}>
                    <Box fontSize={'2xl'} fontWeight={'semibold'}>
                      {product.title}
                    </Box>
                    <Text maxW={'lg'} color={'gray.500'} textAlign={'center'} fontSize={'lg'}>
                      {product.description}
                    </Text>

                    {product.allergics.length > 0 && (
                      <Text maxW={'lg'} color={'gray.500'} textAlign={'center'} fontSize={'lg'}>
                        <Text color={'red.600'} fontWeight={'600'}>
                          {'(*) '} {t('product.allergy', 'Allergy')}
                        </Text>
                        {product.allergics.map((item) => item.title).join(', ')}
                      </Text>
                    )}
                  </VStack>
                </VStack>
                <Box px={4} pt={4}>
                  <VStack divideY={'1px'} divideColor={'gray.200'} gap={4} pb={4}>
                    {product.configurations.map((config) => {
                      const configuration = getFragmentData(MenuProductConfigurationFragment, config);
                      const configurationValueIDs = getFragmentData(
                        MenuProductConfigurationValueFragment,
                        configuration.values
                      ).map((value) => value.id);

                      const required =
                        configuration.type === ProductConfigurationType.Base ||
                        configuration.type === ProductConfigurationType.Selection;

                      return (
                        <Box w={'full'} key={configuration.id}>
                          <Heading size={'lg'} my={4}>
                            {t(configuration.title, configuration.title)}
                            {required && (
                              <Text as="span" fontSize="xs" fontWeight="normal">
                                ({t('online-order.picker-page.product-detail.required', 'Required')})
                              </Text>
                            )}
                          </Heading>

                          {configuration.type == ProductConfigurationType.Base && (
                            <RadioGroup.Root
                              name={configuration.id}
                              variant={'subtle'}
                              defaultValue={
                                configuration.values.at(0)
                                  ? getFragmentData(MenuProductConfigurationValueFragment, configuration.values.at(0))
                                      ?.id
                                  : undefined
                              }
                              onValueChange={({ value }) => {
                                // Delete other selections
                                const newConfigurationValues = configurationValues.filter(
                                  (v) => !configurationValueIDs.includes(v)
                                );

                                // Push new selection
                                newConfigurationValues.push(value ?? '');

                                // Update
                                setConfigurationValues([...newConfigurationValues]);
                              }}
                              display={'grid'}
                              w={'full'}
                              gap={4}
                              gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                              justifyContent="space-between"
                              colorPalette={'gray'}
                            >
                              {configuration.values.map((value, index) => {
                                const configurationValue = getFragmentData(
                                  MenuProductConfigurationValueFragment,
                                  value
                                );
                                return (
                                  <RadioGroup.Item key={index} value={configurationValue.id}>
                                    <RadioGroup.ItemHiddenInput />
                                    <RadioGroup.ItemIndicator />
                                    <RadioGroup.ItemText>
                                      <VStack align="flex-start">
                                        <Text>{configurationValue.name}</Text>
                                        <Text fontSize={'sm'}>
                                          {configurationValue.price > 0 && (
                                            <CurrencyView price={configurationValue.price} />
                                          )}
                                        </Text>
                                      </VStack>
                                    </RadioGroup.ItemText>
                                  </RadioGroup.Item>
                                );
                              })}
                            </RadioGroup.Root>
                          )}

                          {configuration.type == ProductConfigurationType.Selection && (
                            <RadioGroup.Root
                              name={configuration.id}
                              variant={'subtle'}
                              defaultValue={
                                configuration.values.at(0)
                                  ? getFragmentData(MenuProductConfigurationValueFragment, configuration.values.at(0))
                                      ?.id
                                  : undefined
                              }
                              onValueChange={({ value }) => {
                                // Delete other selections
                                const newConfigurationValues = configurationValues.filter(
                                  (v) => !configurationValueIDs.includes(v)
                                );

                                // Push new selection
                                newConfigurationValues.push(value ?? '');

                                // Update
                                setConfigurationValues([...newConfigurationValues]);
                              }}
                              display={'grid'}
                              w={'full'}
                              gap={4}
                              gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                              justifyContent="space-between"
                              colorPalette={'gray'}
                            >
                              {configuration.values.map((value, index) => {
                                const configurationValue = getFragmentData(
                                  MenuProductConfigurationValueFragment,
                                  value
                                );
                                return (
                                  <RadioGroup.Item key={index} value={configurationValue.id}>
                                    <RadioGroup.ItemHiddenInput />
                                    <RadioGroup.ItemIndicator />
                                    <RadioGroup.ItemText>
                                      <VStack align="flex-start">
                                        <Text>{configurationValue.name}</Text>
                                        <Text fontSize={'sm'}>
                                          {configurationValue.price > 0 && (
                                            <CurrencyView price={configurationValue.price} />
                                          )}
                                        </Text>
                                      </VStack>
                                    </RadioGroup.ItemText>
                                  </RadioGroup.Item>
                                );
                              })}
                            </RadioGroup.Root>
                          )}

                          {configuration.type == ProductConfigurationType.Option && (
                            <Box
                              display={'grid'}
                              w={'full'}
                              gap={4}
                              gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                              justifyContent="space-between"
                            >
                              {configuration.values.map((value, index) => {
                                const configurationValue = getFragmentData(
                                  MenuProductConfigurationValueFragment,
                                  value
                                );
                                return (
                                  <Checkbox.Root
                                    key={index}
                                    id={configurationValue.id}
                                    colorPalette={'gray'}
                                    variant={'subtle'}
                                    onCheckedChange={(value) => {
                                      if (value.checked) {
                                        setConfigurationValues([...configurationValues, configurationValue.id]);
                                      } else {
                                        setConfigurationValues(
                                          configurationValues.filter((v) => v !== configurationValue.id)
                                        );
                                      }
                                    }}
                                  >
                                    <HStack align="flex-start">
                                      <Checkbox.HiddenInput />
                                      <Checkbox.Control />
                                      <Checkbox.Label>
                                        <Text>{configurationValue.name}</Text>
                                        {configurationValue.price > 0 && (
                                          <HStack fontSize="sm">
                                            <Text>+</Text>
                                            <CurrencyView price={configurationValue.price} />
                                          </HStack>
                                        )}
                                      </Checkbox.Label>
                                    </HStack>
                                  </Checkbox.Root>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </VStack>

                  <VStack align={'start'} py={4} borderTop={'1px solid'} borderColor={'gray.300'}>
                    <Heading size={'lg'}>{t('digital-menu.quantity', 'Quantity')}</Heading>
                    <HStack w={'full'} justify="start" gap={3} py={2}>
                      <Icon>
                        <CiCircleMinus
                          size={30}
                          onClick={() => {
                            setCount((value) => (value > 1 ? value - 1 : 1));
                          }}
                        />
                      </Icon>
                      <Text>{count}</Text>
                      <Icon>
                        <CiCirclePlus
                          size={30}
                          onClick={() => {
                            setCount(count + 1);
                          }}
                        />
                      </Icon>
                    </HStack>
                  </VStack>
                </Box>
              </VStack>
            </Dialog.Body>

            <Dialog.Footer p={0}>
              <Dialog.ActionTrigger asChild>
                <Button
                  className="product-config-chakra__add-to-cart-btn"
                  onClick={() => {
                    addToCartHandler();
                  }}
                  py={'6'}
                  color={{ _light: 'white', _dark: 'black' }}
                  bgColor={{ _light: 'black', _dark: 'white' }}
                  px={4}
                  fontSize={'lg'}
                  fontWeight={'semibold'}
                  variant={'outline'}
                  w={'full'}
                >
                  <Flex w={'full'} justify={'space-between'} gapX={2} align={'center'}>
                    <Text fontSize={'2xl'} fontWeight="medium">
                      <CurrencyView price={total} />
                    </Text>
                    <HStack>
                      <Icon>
                        <CiShoppingCart />
                      </Icon>
                      <Text>{t('button.add-to-cart', 'Add to cart')}</Text>
                    </HStack>
                  </Flex>
                </Button>
              </Dialog.ActionTrigger>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default ProductConfigurationSetupV2;
